<script setup>
import { usePage } from '@inertiajs/vue3';
import AlertBox from '@js/components/alerts/AlertBox.vue';
import DialogDrawer from '@js/components/dialogs/DialogDrawer.vue';
import ComboboxField from '@js/components/fields/ComboboxField.vue';
import EditorField from '@js/components/fields/EditorField.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import TimeField from '@js/components/fields/TimeField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { formatDate, formatPhoneNumber, formatTime } from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import { computed, reactive, ref } from 'vue';
import { object, string } from 'yup';

const { phoneCall, tenants, users, meansOfCommunications, callback } =
    defineProps({
        phoneCall: {
            type: Object,
            required: true,
        },
        tenants: {
            type: Array,
            required: true,
        },
        users: {
            type: Array,
            required: true,
        },
        meansOfCommunications: {
            type: Array,
            required: true,
        },
        callback: {
            type: Function,
            required: false,
        },
    });

const { route } = useRoute();

const currentUser = computed(() => usePage().props.currentUser);

const callSummary = computed(() => {
    const duration = formatTime(
        Math.floor(phoneCall.duration / 3600) || '',
        String(Math.floor((phoneCall.duration % 3600) / 60)).padStart(2, '0'),
    );

    const name = phoneCall.name ? ` (${phoneCall.name})` : '';

    const number = phoneCall.number
        ? formatPhoneNumber(phoneCall.number)
        : 'numéro masqué';

    return `Appel ${phoneCall.type.label.toLowerCase()} du ${formatDate(phoneCall.calledAtDate)} à ${phoneCall.calledAtTime}, au ${number}${name}${duration ? `, d'une durée de ${duration}` : ''}.`;
});

const isOpen = ref(false);

const isSubmitting = ref(false);

const form = reactive({
    tenant: phoneCall.tenant
        ? (tenants.find((tenant) => tenant.id === phoneCall.tenant.id) ?? null)
        : null,
    meansOfCommunication:
        meansOfCommunications.find(
            (meansOfCommunication) =>
                meansOfCommunication.value === 'telephone',
        ) ?? meansOfCommunications[0],
    users: users.filter((user) => user.id === currentUser.value.id),
    subject: 'Appel',
    comment: '',
    scheduledAt: phoneCall.calledAtDate,
    hours:
        Math.floor(phoneCall.minutes / 60) > 0
            ? String(Math.floor(phoneCall.minutes / 60))
            : '',
    minutes: String(phoneCall.minutes % 60).padStart(2, '0'),
});

const validationSchema = object({
    tenant: object().nullable().required('Ce champ est obligatoire.'),
    meansOfCommunication: object().required('Ce champ est obligatoire.'),
    subject: string().required('Ce champ est obligatoire.'),
    comment: string(),
    scheduledAt: string().required('Ce champ est obligatoire.'),
    hours: string(),
    minutes: string().required('Ce champ est obligatoire.'),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.apikom.postValidatePhoneCall', {
                phoneCall: phoneCall.id,
            }),
            {
                tenantId: form.tenant.id,
                meansOfCommunication: form.meansOfCommunication.value,
                userIds: form.users.map((user) => user.id),
                subject: form.subject,
                comment: form.comment ?? '',
                scheduledAt: `${form.scheduledAt} ${phoneCall.calledAtTime}:00`,
                minutes:
                    parseInt(form.hours || '0', 10) * 60 +
                    parseInt(form.minutes || '0', 10),
            },
        )
        .then(() => {
            isOpen.value = false;

            if (callback) {
                callback();
            }
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            console.error(error);
        });
}
</script>

<template>
    <DialogDrawer v-model="isOpen" size="extra-large">
        <template #trigger>
            <slot name="trigger" />
        </template>

        <template #title> Créer un rendez-vous depuis l'appel </template>

        <template #full-content>
            <FormRoot
                :validation-schema="validationSchema"
                :submit="submit"
                v-slot="{ errors }"
            >
                <FormColumn>
                    <AlertBox>
                        {{ callSummary }}
                    </AlertBox>

                    <AlertBox v-if="!phoneCall.tenant" type="warning">
                        Cet appel n'a pas pu être rattaché à un client :
                        sélectionnez-le ci-dessous.
                    </AlertBox>

                    <FormContent>
                        <ComboboxField
                            v-model="form.tenant"
                            :options="tenants"
                            label="Client"
                            field-name="tenant"
                            field-id="tenant"
                            filtered-option-key="name"
                            by="id"
                            required
                        >
                            <template #optionContent="{ option }">
                                {{ option.name }}
                            </template>
                        </ComboboxField>

                        <ComboboxField
                            v-model="form.users"
                            :options="users"
                            label="Utilisateurs"
                            field-name="users"
                            field-id="users"
                            filteredOptionKey="fullName"
                            multiple
                        >
                            <template #optionContent="{ option }">
                                {{ option.fullName }}
                            </template>
                        </ComboboxField>

                        <ListboxField
                            v-model="form.meansOfCommunication"
                            field-id="meansOfCommunication"
                            field-name="meansOfCommunication"
                            label="Moyen de communication"
                            :options="meansOfCommunications"
                            required
                        >
                            <template #buttonContent>
                                {{ form.meansOfCommunication.label }}
                            </template>

                            <template #optionContent="{ option }">
                                {{ option.label }}
                            </template>
                        </ListboxField>

                        <InputField
                            v-model="form.scheduledAt"
                            label="Planifié le"
                            field-name="scheduledAt"
                            field-id="scheduledAt"
                            type="date"
                            required
                        />

                        <TimeField
                            v-model:hours="form.hours"
                            v-model:minutes="form.minutes"
                            hours-field-id="hours"
                            hours-field-name="hours"
                            minutes-field-id="minutes"
                            minutes-field-name="minutes"
                            label="Durée"
                            required
                        />
                    </FormContent>

                    <FormContent>
                        <InputField
                            v-model="form.subject"
                            label="Sujet"
                            field-name="subject"
                            field-id="subject"
                            required
                        />

                        <EditorField
                            v-model="form.comment"
                            label="Commentaire"
                            field-name="comment"
                            field-id="comment"
                        />
                    </FormContent>
                </FormColumn>

                <FormFooter
                    :is-submitting="isSubmitting"
                    :errors="errors"
                    without-back-button
                />
            </FormRoot>
        </template>
    </DialogDrawer>
</template>
